import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { KeyRound, Loader2 } from "lucide-react";
import { parErr, portalApi } from "@/lib/partnerApi";
import { useAuth } from "@/lib/auth";
import { goldBtn, inputCls, PortalShell } from "@/pages/partner/PartnerLoginPage";

const pinIn = (set) => (e) => set(e.target.value.replace(/\D/g, "").slice(0, 6));

/** Partner invite link: choose a 6-digit PIN, then sign straight into the TEA desktop. */
export default function PartnerSetPinPage() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const nav = useNavigate();
  const { applySession } = useAuth();
  const [invite, setInvite] = useState(null);
  const [pin, setPin] = useState("");
  const [pin2, setPin2] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  useEffect(() => { portalApi.get("/auth/invite", { token }).then(setInvite).catch((e) => setInvite({ error: parErr(e) })); }, [token]);
  const submit = async (e) => {
    e.preventDefault();
    if (pin !== pin2) { setError("PINs don't match"); return; }
    setBusy(true); setError(null);
    try {
      const d = await portalApi.post("/auth/set-pin", { token, pin });
      const s = (await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/auth/sign-in`, { identifier: d.email, pin, group: "partner" })).data;
      applySession(s.token, s.user);
      nav("/admin", { replace: true });
    } catch (err) { setError(parErr(err)); } finally { setBusy(false); }
  };
  if (!invite) return <PortalShell title="Checking your invite…" testid="partner-setpin-loading"><Loader2 className="h-5 w-5 animate-spin text-[#d4a017]" /></PortalShell>;
  if (invite.error) return <PortalShell title="Invite link unavailable" testid="partner-setpin-invalid"><p className="text-white/70">{invite.error}. Ask your Exchange Alliance contact to resend your invite, or tap "Get a code by email" on the sign-in page.</p></PortalShell>;
  return (
    <PortalShell title={`Welcome, ${invite.first_name}`} testid="partner-setpin-page">
      <p className="text-white/60 text-sm">Choose a 6-digit PIN for <b className="text-white">{invite.email}</b>. You'll use it with your email to sign in (or use Google).</p>
      <form onSubmit={submit} className="space-y-4">
        <input type="password" inputMode="numeric" required autoComplete="new-password" placeholder="New 6-digit PIN" value={pin} onChange={pinIn(setPin)} data-testid="partner-setpin-pin" className={`${inputCls} tracking-[0.5em] font-mono`} />
        <input type="password" inputMode="numeric" required autoComplete="new-password" placeholder="Confirm PIN" value={pin2} onChange={pinIn(setPin2)} data-testid="partner-setpin-confirm" className={`${inputCls} tracking-[0.5em] font-mono`} />
        {error && <p data-testid="partner-setpin-error" className="text-sm text-red-300">{error}</p>}
        <button type="submit" disabled={busy || pin.length !== 6 || pin2.length !== 6} data-testid="partner-setpin-submit" className={goldBtn}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <KeyRound className="h-4 w-4" />} Set PIN &amp; sign in</button>
      </form>
    </PortalShell>
  );
}
